import * as React from "react";

type DockSide = "left" | "right" | null;
type CSSStyle = Partial<Record<keyof CSSStyleDeclaration, string>>;

export interface UseDraggableWindowOptions {
  constrainToViewport?: boolean;
  snap?: {
    enabled?: boolean;
    threshold?: number;
    anchorPreviewWidth?: number;
    dockedWidth?: number | string;
    previewStyle?: CSSStyle;
    highlightStyle?: CSSStyle;
    dimStyle?: CSSStyle;
  };
  onDockChange?: (side: DockSide) => void;
  initialDocked?: Exclude<DockSide, null>;
  initialPosition?: { top: number; left: number };
}

export interface UseDraggableWindowReturn {
  isDragging: boolean;
  docked: DockSide;
  undock: () => void;
  dock: (side: Exclude<DockSide, null>) => void;
}

export function useDraggableWindow(
  windowRef: React.MutableRefObject<HTMLElement | null>,
  handleRef: React.MutableRefObject<HTMLElement | null>,
  options: UseDraggableWindowOptions = {}
): UseDraggableWindowReturn {
  const {
    constrainToViewport = true,
    snap = {},
    onDockChange,
    initialDocked,
    initialPosition
  } = options;

  const initialDockedRef = React.useRef(initialDocked);
  const initialPositionRef = React.useRef(initialPosition);
  const didInitRef = React.useRef(false);

  const snapEnabled = snap.enabled ?? true;
  const snapThreshold = snap.threshold ?? 48;
  const anchorPreviewWidth = snap.anchorPreviewWidth ?? 14;
  const dockedWidth =
    typeof snap.dockedWidth === "number"
      ? `${snap.dockedWidth}px`
      : snap.dockedWidth;

  const defaultDim: CSSStyle = { background: "rgba(25,141,202,0.1)" };
  const defaultHi: CSSStyle = { background: "rgba(25,141,202,0.55)" };

  const [isDragging, setIsDragging] = React.useState(false);
  const [docked, setDocked] = React.useState<DockSide>(
    () => initialDockedRef.current ?? null
  );

  const startPointer = React.useRef<{ x: number; y: number } | null>(null);
  const startRect = React.useRef<DOMRect | null>(null);
  const activePointerId = React.useRef<number | null>(null);
  const didUndockOnThisDrag = React.useRef(false);

  const nearSide = React.useRef<DockSide>(null);
  const anchors = React.useRef<{
    left?: HTMLDivElement;
    right?: HTMLDivElement;
  }>({});

  const ensureFixed = React.useCallback(() => {
    const el = windowRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    if (cs.position !== "fixed") {
      const r = el.getBoundingClientRect();
      el.style.position = "fixed";
      el.style.top = `${r.top}px`;
      el.style.left = `${r.left}px`;
      el.style.right = "";
      el.style.bottom = "";
      el.style.transform = "translate(0,0)";
      el.style.height = "";
    }
    el.style.willChange = "top,left,height,width,transform";
  }, [windowRef]);

  const showAnchors = React.useCallback(() => {
    if (!snapEnabled) return;
    const mk = (side: "left" | "right") => {
      const div = document.createElement("div");
      const base: CSSStyle = {
        position: "fixed",
        top: "0",
        bottom: "0",
        width: `${anchorPreviewWidth}px`,
        [side]: "0",
        zIndex: "2147483647",
        pointerEvents: "none",
        transition: "background 120ms ease, opacity 120ms ease",
        border: "4px dashed rgba(25,141,202,0.22)"
      };
      Object.assign(div.style, base, defaultDim, snap.previewStyle || {});
      document.body.appendChild(div);
      return div;
    };
    if (!anchors.current.left) anchors.current.left = mk("left");
    if (!anchors.current.right) anchors.current.right = mk("right");
  }, [snapEnabled, anchorPreviewWidth, snap.previewStyle]);

  const applyStyle = (el: HTMLDivElement | undefined, style: CSSStyle) => {
    if (!el) return;
    Object.assign(el.style, style);
  };

  const highlightAnchor = React.useCallback(
    (side: DockSide) => {
      nearSide.current = side;
      const dim = snap.dimStyle || defaultDim;
      const hi = snap.highlightStyle || defaultHi;
      applyStyle(anchors.current.left, side === "left" ? hi : dim);
      applyStyle(anchors.current.right, side === "right" ? hi : dim);
    },
    [snap.dimStyle, snap.highlightStyle]
  );

  const hideAnchors = React.useCallback(() => {
    Object.values(anchors.current).forEach((el) => el?.remove());
    anchors.current = {};
    nearSide.current = null;
  }, []);

  const applyDock = React.useCallback(
    (side: Exclude<DockSide, null>) => {
      const el = windowRef.current;
      if (!el) return;
      el.style.position = "fixed";
      el.style.top = "0";
      el.style.bottom = "0";
      el.style.left = side === "left" ? "0" : "";
      el.style.right = side === "right" ? "0" : "";
      el.style.transform = "translate(0,0)";
      el.style.height = "100vh";
      if (dockedWidth) el.style.width = dockedWidth;
      setDocked(side);
      onDockChange?.(side);
    },
    [windowRef, dockedWidth, onDockChange]
  );

  const applyUndock = React.useCallback(() => {
    const el = windowRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.position = "fixed";
    el.style.top = `${r.top}px`;
    el.style.left = `${r.left}px`;
    el.style.right = "";
    el.style.bottom = "";
    el.style.height = "";
    el.style.transform = "translate(0,0)";
    setDocked(null);
    onDockChange?.(null);
  }, [windowRef, onDockChange]);

  const undock = React.useCallback(() => applyUndock(), [applyUndock]);
  const dock = React.useCallback(
    (side: Exclude<DockSide, null>) => applyDock(side),
    [applyDock]
  );

  React.useEffect(() => {
    const handle = handleRef.current;
    const target = windowRef.current;
    if (!handle || !target) return;

    (handle as HTMLElement).style.touchAction = "none";

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      ensureFixed();
      startRect.current = target.getBoundingClientRect();
      startPointer.current = { x: e.clientX, y: e.clientY };
      activePointerId.current = e.pointerId;
      didUndockOnThisDrag.current = false;
      handle.setPointerCapture?.(e.pointerId);
      setIsDragging(true);
      showAnchors();
      highlightAnchor(null);
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      if (!startPointer.current || !startRect.current) return;
      if (docked && !didUndockOnThisDrag.current) {
        applyUndock();
        startRect.current = target.getBoundingClientRect();
        didUndockOnThisDrag.current = true;
      }
      const dx = e.clientX - startPointer.current.x;
      const dy = e.clientY - startPointer.current.y;
      let nextLeft = startRect.current.left + dx;
      let nextTop = startRect.current.top + dy;

      if (constrainToViewport) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = startRect.current.width;
        const h = startRect.current.height;
        nextLeft = Math.max(0, Math.min(vw - w, nextLeft));
        nextTop = Math.max(0, Math.min(vh - h, nextTop));
      }

      target.style.left = `${nextLeft}px`;
      target.style.top = `${nextTop}px`;
      target.style.transform = "translate(0,0)";

      if (snapEnabled) {
        const nearLeft = e.clientX <= snapThreshold ? "left" : null;
        const nearRight =
          e.clientX >= window.innerWidth - snapThreshold ? "right" : null;
        highlightAnchor((nearLeft || nearRight) as DockSide);
      }

      e.preventDefault();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      handle.releasePointerCapture?.(e.pointerId);
      const wantDock = snapEnabled ? nearSide.current : null;
      hideAnchors();
      setIsDragging(false);
      activePointerId.current = null;
      didUndockOnThisDrag.current = false;
      if (wantDock) applyDock(wantDock);
    };

    handle.addEventListener("pointerdown", onPointerDown, { passive: false });
    handle.addEventListener("pointermove", onPointerMove, { passive: false });
    handle.addEventListener("pointerup", onPointerUp, { passive: true });
    handle.addEventListener("pointercancel", onPointerUp, { passive: true });

    return () => {
      handle.removeEventListener("pointerdown", onPointerDown as EventListener);
      handle.removeEventListener("pointermove", onPointerMove as EventListener);
      handle.removeEventListener("pointerup", onPointerUp as EventListener);
      handle.removeEventListener("pointercancel", onPointerUp as EventListener);
    };
  }, [
    handleRef,
    windowRef,
    constrainToViewport,
    snapEnabled,
    snapThreshold,
    showAnchors,
    hideAnchors,
    highlightAnchor,
    ensureFixed,
    docked,
    applyUndock,
    applyDock
  ]);

  React.useLayoutEffect(() => {
    if (didInitRef.current) return;
    const el = windowRef.current;
    if (!el) return;
    const side = initialDockedRef.current;
    if (side === "left" || side === "right") {
      applyDock(side);
      didInitRef.current = true;
      return;
    }
    const p = initialPositionRef.current;
    if (p) {
      ensureFixed();
      el.style.top = `${p.top}px`;
      el.style.left = `${p.left}px`;
      el.style.right = "";
      el.style.bottom = "";
      el.style.transform = "translate(0,0)";
    }
    didInitRef.current = true;
  }, [windowRef, applyDock, ensureFixed]);

  return { isDragging, docked, undock, dock };
}
